import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import Header from "../components/Header";
import { MeasurementChart } from "../components/MeasurementChart";
import { MeasurementForm } from "../components/MeasurementForm";
import { MeasurementList } from "../components/MeasurementList";
import { measureConfig, numbered, toTs } from "../lib/measurements";

const Legend = () => (
  <div className="flex flex-wrap gap-5 text-xs text-zinc-600" data-testid="measurement-legend">
    <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 border-2 border-zinc-900 bg-white" /> Nulmeting</span>
    <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-zinc-900" /> Meting</span>
    <span className="flex items-center gap-1.5"><span className="w-5 border-t-2 border-dashed border-zinc-400" /> Subdoelen</span>
    <span className="flex items-center gap-1.5"><span className="w-5 border-t border-zinc-900" /> Hoofddoel</span>
    <span className="flex items-center gap-1.5"><span className="h-3 border-l border-dashed border-zinc-900" /> Start interventie</span>
  </div>
);

export default function PvpMeasurementsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pvp, setPvp] = useState(null);
  const [orgs, setOrgs] = useState([]);

  useEffect(() => {
    let live = true;
    api.get("/organizations").then(({ data }) => live && setOrgs(data));
    api.get(`/pvps/${id}`).then(({ data }) => live && setPvp(data), () => live && toast.error("PVP niet gevonden"));
    return () => {
      live = false;
    };
  }, [id]);

  if (!pvp) return <div className="p-10 font-mono text-sm text-zinc-500">Metingen laden…</div>;
  const canEdit = ["superadmin", "consultant", "org_admin"].includes(user.role) || (user.role === "member" && user.employee_id === pvp.employee_id);
  const cfg = measureConfig(pvp);
  const points = numbered((pvp.measurements || []).map((m) => ({ ...m, t: toTs(m.datum) })));
  const interventions = pvp.interventions || [];
  const remove = async (mid) => {
    try {
      const { data } = await api.delete(`/pvps/${id}/measurements/${mid}`);
      setPvp(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Verwijderen mislukt");
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="pvp-measurements-page">
      <Header orgs={orgs} currentOrgId={pvp.organization_id} onSelectOrg={(o) => navigate(`/employees?org=${o}`)} />
      <main className="max-w-6xl mx-auto px-6 py-10">
        <button onClick={() => navigate(`/pvp/${id}`)} className="font-mono text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1 mb-4" data-testid="measurements-back-button">
          <ArrowLeft className="h-3 w-3" /> Terug naar PVP
        </button>
        <div className="border-b border-zinc-900 pb-6 mb-8 flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[260px]">
            <div className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-2">Meetgegevens · {pvp.employee_name}</div>
            <h1 className="font-grotesk text-3xl sm:text-4xl font-bold tracking-tight" data-testid="measurements-title">{pvp.behavior_text}</h1>
            {pvp.sections?.specificatie?.hoofddoel_zin && <p className="text-zinc-600 mt-2" data-testid="measurements-goal-sentence">{pvp.sections.specificatie.hoofddoel_zin}</p>}
          </div>
          {canEdit && <MeasurementForm pvp={pvp} cfg={cfg} onSaved={setPvp} />}
        </div>
        <div className="border border-zinc-900 p-4 sm:p-6 mb-3">
          <MeasurementChart pvp={pvp} cfg={cfg} points={points} interventions={interventions} />
        </div>
        <Legend />
        <h2 className="font-grotesk text-xl font-bold mt-12 mb-2">Metingen</h2>
        <MeasurementList points={points} interventions={interventions} sfx={cfg.sfx} canEdit={canEdit} onDelete={remove} />
      </main>
    </div>
  );
}
